import React, { useEffect, useState, useCallback } from 'react';
import { Search } from 'lucide-react';
import FormControl from '../common/FormControl';
import Pagination from '../common/Pagination';
import NoDataFound from '../common/NoDataFound';
import Skeleton from '../common/Skeleton';
import SeverityBadge from './SeverityBadge';
import Badge from '../common/Badge';
import Toast, { type ToastMessage } from '../common/Toast';
import { statusVariant } from '../../utils/statusVariant';
import { incidentsApi, Incident } from '../../services/incidentService';
import { usePermission } from '../../contexts/PermissionContext';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';

const ACT_ARCHIVE = 'IRG_INCIDENT_ARCHIVE';

const IncidentArchived = () => {
    const { allowed: canView, loading: viewLoading } = usePermission(ACT_ARCHIVE, 'view');
    const { formatDate } = useEntitySettings();
    const [items, setItems] = useState<Incident[]>([]);
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const reload = useCallback(async () => {
        if (!canView) return;
        setLoading(true);
        setToast(null);
        try {
            const data = await incidentsApi.list({ archived: true, limit: 200 });
            setItems(data);
        } catch (e: any) {
            setToast({ type: 'error', message: e?.response?.data?.detail || e?.message || 'Failed to load' });
        } finally {
            setLoading(false);
        }
    }, [canView]);

    useEffect(() => { reload(); }, [reload]);

    useEffect(() => { setPage(1); }, [search, pageSize]);

    const filtered = items.filter(i => {
        if (!search) return true;
        const q = search.toLowerCase();
        return i.incident_code.toLowerCase().includes(q)
            || i.incident_description.toLowerCase().includes(q);
    });

    const totalRecords = filtered.length;
    const paginatedRows = filtered.slice((page - 1) * pageSize, page * pageSize);

    const fmtDate = (s?: string) => (s ? formatDate(s) : '—');

    if (!viewLoading && !canView) {
        return (
            <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                <NoDataFound title="Not authorised" message="You do not have access to archived incidents." />
            </div>
        );
    }

    return (
        <div className="netbody-card flex flex-col h-full">

            {/* Toolbar — search RIGHT */}
            <div className="netbody-header">
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Search archive…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_incident_archived_search"
                    />
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list">
                        <div className="table-list-scroll">
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="table-list-th">Code</th>
                                        <th className="table-list-th">Description</th>
                                        <th className="table-list-th">Occurrence</th>
                                        <th className="table-list-th">Severity</th>
                                        <th className="table-list-th">Date</th>
                                        <th className="table-list-th">Resolution</th>
                                        <th className="table-list-th table-list-cell--status">Status</th>
                                    </tr>
                                </thead>
                                <tbody aria-busy={loading} aria-live="polite">
                                    {loading && <Skeleton.TableRows cols={['3xs', 'md', 'sm', 'pill', '2xs', '2xs', 'xs', 'pill']} />}
                                    {!loading && filtered.length === 0 && (
                                        <tr>
                                            <td colSpan={8} className="table-list-cell">
                                                <NoDataFound
                                                    title={search ? 'No Matching Archives' : 'No Archived Incidents'}
                                                    message={search ? 'No archived incidents match your search.' : 'Resolved incidents will appear here once archived.'}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    {!loading && paginatedRows.map((incident, idx) => (
                                        <tr key={incident.id} className="table-list-row">
                                            <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                            <td className="table-list-cell">{incident.incident_code}</td>
                                            <td className="table-list-cell table-list-cell--truncate" title={incident.incident_description}>
                                                <span className="table-list-cell-title">{incident.incident_description}</span>
                                            </td>
                                            <td className="table-list-cell">{incident.occurrence_type || '—'}</td>
                                            <td className="table-list-cell">
                                                <SeverityBadge severity={incident.occurrence_type === 'Near Miss' ? 'Low' : 'Medium'} />
                                            </td>
                                            <td className="table-list-cell">{fmtDate(incident.created_on)}</td>
                                            <td className="table-list-cell table-list-cell--truncate" title={incident.reporter_feedback || ''}>
                                                {incident.reporter_feedback || '—'}
                                            </td>
                                            <td className="table-list-cell">
                                                <Badge variant={statusVariant(incident.remediation_status)} size="sm">
                                                    {incident.remediation_status || '—'}
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalRecords={totalRecords}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            pageSizeOptions={[10, 25, 50, 100]}
                            recordLabel="incidents"
                        />
                    </div>
                </div>
            </div>
            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default IncidentArchived;
